// SPDX-License-Identifier: AGPL-3.0-or-later
import { z } from 'zod';
/**
 * Canonical code-system URIs used by intakeform Questionnaires and helper
 * builders for the most common Coding shapes. Extend this file when a new
 * code system is introduced — never inline raw `system` URIs at call sites.
 */
export const CodeSystem = {
    /** BCP-47 language tags, e.g. "en", "es-419". */
    language: 'urn:ietf:bcp:47',
    /** LOINC — clinical observations and questionnaire codes. */
    loinc: 'http://loinc.org',
    /** SNOMED CT — clinical findings, procedures, etc. */
    snomed: 'http://snomed.info/sct',
    /** IETF RFC 6838 media types — used when we need to communicate MIME via Coding. */
    mimeType: 'urn:ietf:rfc:3986',
    /**
     * FHIR v3 RoleCode — RelatedPerson `relationship` codes such as PRN
     * (parent), CHILDFOST (child), SPS (spouse), etc. Used when
     * materialising the relationship between a patient and the caregiver
     * filling a form on their behalf.
     * https://terminology.hl7.org/CodeSystem-v3-RoleCode.html
     */
    v3RoleCode: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
};
/** FHIR R4 Coding — strict shape, but tolerant of additional unknown keys for round-trip. */
export const CodingSchema = z
    .object({
    system: z.string().optional(),
    version: z.string().optional(),
    code: z.string().optional(),
    display: z.string().optional(),
    userSelected: z.boolean().optional(),
})
    .passthrough();
/** Build a language Coding for `urn:ietf:bcp:47`. */
export function languageCoding(code, display) {
    return display === undefined
        ? { system: CodeSystem.language, code }
        : { system: CodeSystem.language, code, display };
}
/** Build a LOINC Coding. */
export function loincCoding(code, display) {
    return display === undefined
        ? { system: CodeSystem.loinc, code }
        : { system: CodeSystem.loinc, code, display };
}
/** Build a SNOMED CT Coding. */
export function snomedCoding(code, display) {
    return display === undefined
        ? { system: CodeSystem.snomed, code }
        : { system: CodeSystem.snomed, code, display };
}
/**
 * RelatedPerson relationship codes. Application-level enum that hides
 * the v3 RoleCode short-form (PRN, GUARD, etc.); the persisted column
 * stores the long form so logs + dashboards are self-describing.
 *
 * The `display` is what we render in the patient banner; the `v3` code
 * is what we put on the FHIR `Coding.code`.
 */
export const RelationshipCode = {
    parent: { value: 'parent', display: 'Parent', v3: 'PRN' },
    guardian: { value: 'guardian', display: 'Guardian', v3: 'GUARD' },
    caregiver: { value: 'caregiver', display: 'Caregiver', v3: 'CAREGIVER' },
    spouse: { value: 'spouse', display: 'Spouse', v3: 'SPS' },
    child: { value: 'child', display: 'Child', v3: 'CHILD' },
    other: { value: 'other', display: 'Other', v3: 'ONESELF' },
};
/** Allow-list — used by both the API Zod schema and the web form. */
export const RELATIONSHIP_CODES = [
    'parent',
    'guardian',
    'caregiver',
    'spouse',
    'child',
    'other',
];
/** Build a FHIR `Coding` for a relationship code. */
export function relationshipCoding(value) {
    const entry = RelationshipCode[value];
    return { system: CodeSystem.v3RoleCode, code: entry.v3, display: entry.display };
}
/** Human-readable label for the relationship code (used in the patient banner). */
export function relationshipDisplay(value) {
    if (value in RelationshipCode) {
        return RelationshipCode[value].display;
    }
    return value;
}
//# sourceMappingURL=codings.js.map